import { useLocation, useParams, Outlet } from "react-router-dom"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { routing } from "./routing"
import { pages } from "./pages"
import Selection, { SelectionItem, SubNavItem } from "@hydrologiq/shared/selection/Selection"
import { useGetCopyHomeRoute, useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCustomer, useCustomerSite } from "../../providers/helpers/useCustomer"
import { useRenameCustomer, useRenameSite } from "../../hooks/useRenameCustomer"
import { useSystemInfo, useSystemViewData } from "@hydrologiq/systems/providers/helpers/useSystem"
import { useRenameSystem } from "@hydrologiq/systems/hooks/useRenameSystem"

function CustomerSelection({ children }: { children?: React.ReactNode }) {
  const { customerId, siteId, systemId } = useParams()
  const route = useRoute()
  let location = useLocation().pathname
  const customerAdvancedFeatureEnabled = useGetFeature("customer_advanced")
  const customerReportingFeatureEnabled = useGetFeature("customer_reporting")
  const installationForecastEnabled = useGetFeature("installation_forecast")
  const hideReports = useGetFeature("hide_reports_system")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.SITES]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITES],
  })

  const customer = useCustomer()
  const site = useCustomerSite()
  const system = useSystemInfo()
  const viewData = useSystemViewData()

  const { renameCustomer, renaming: renamingCustomer } = useRenameCustomer(customer)
  const { renameSite, renaming: renamingSite } = useRenameSite(site)
  const { renameSystem, renaming: renamingSystem } = useRenameSystem(systemId ?? "")

  // The hardcoded demo system has no editable backing record — keep it read-only.
  const isDemoSystem = systemId === "848bd52d-c1cb-4013-8386-4bcb2b7e13a3"


  const sectionPages = pages(
    location,
    customerId !== undefined,
    siteId !== undefined,
    false,
    systemId !== undefined,
    customerAdvancedFeatureEnabled,
    customerReportingFeatureEnabled
  )

  // Map to translate page names using copy system
  const pageNameMap: Record<string, string> = {
    Sites: copy[COPY_KEYS.HEADINGS.SITES],
  }

  // Create sub-navigation items from sectionPages with translated names
  let subNavItems: SubNavItem[] = Object.entries(sectionPages.pages).filter(([key, value]) => {
    if (system && key === "forecasting") {
      return installationForecastEnabled
    }
    if (system && key === "reporting") {
      return !hideReports
    }
    return true
  }).map(([key, value]) => ({
    name: pageNameMap[value]?.replace("(", "").replace(")", "") || value, // Use translated name if available, otherwise use original
    href: routing(route, location, customerId, siteId, undefined, systemId, false, key),
    disabled: (() => {
      if (system) {
        if (["monitoring", "reporting", "forecasting"].includes(key)) {
          // If monitoring was previously activated (has timescale fields or installations), keep all enabled
          const hasMonitoringHistory = (viewData?.system?.timescaleFields?.length ?? 0) > 0
            || (viewData?.installations?.length ?? 0) > 0
          return !hasMonitoringHistory && !system?.grouped
        }
      }
      return false
    })(),
  }))

  // Create parents for breadcrumb navigation
  const parents: SelectionItem[] = []

  const { name, path } = useGetCopyHomeRoute()
  let item: SelectionItem | undefined = customer
    ? { id: customer.id, name: customer.name, onRename: renameCustomer, renaming: renamingCustomer }
    : undefined

  if (system && customer && site) {
    const customerHref = `${route}/${customer.id}`.replace(/\/+/g, "/")
    const siteHref = `${route}/${customer.id}/sites/${site.id}`.replace(/\/+/g, "/")
    parents.push(
      { id: customer.id, name: customer.name, href: customerHref },
      { id: site.id, name: site.name, href: siteHref }
    )
    item = {
      id: systemId!,
      name: system.name,
      onRename: isDemoSystem ? undefined : renameSystem,
      renaming: isDemoSystem ? undefined : renamingSystem,
    }

    if (system?.installations && Object.keys(system.installations).length > 1) {
      const hasMonitoringHistory = (viewData?.system?.timescaleFields?.length ?? 0) > 0
        || (viewData?.installations?.length ?? 0) > 0
      subNavItems = subNavItems.map((item) => ({
        ...item,
        disabled: ["monitoring", "reporting"].includes(item.name.toLowerCase()) && !hasMonitoringHistory,
      }))
    }
    if (system?.grouped && system?.inlineMonitoring) subNavItems = []
  } else if (siteId && customer && site) {
    const customerHref = `${route}/${customer.id}`.replace(/\/+/g, "/")
    parents.push({ id: customer.id, name: customer.name, href: customerHref })
    item = { id: site.id, name: site.name, onRename: renameSite, renaming: renamingSite }
  }

  return (
    <Selection
      sectionHeading={name}
      sectionBase={path}
      item={item}
      parents={parents}
      subNavItems={subNavItems}
      subNavHome={sectionPages.defaultPage}
    >
      {children || <Outlet />}
    </Selection>
  )
}

export default CustomerSelection
